import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { type WebsitePreviewApiResponse } from '../types';
import { websiteApi } from '../websiteApi';
import { validateWebsiteUrl } from '../websiteUrlValidator';

import { useWebsiteErrorAlert } from './useWebsiteErrorAlert';

/**
 * Website 메타데이터 조회 훅 (캐싱 최적화)
 *
 * queryClient.fetchQuery를 사용하여 완전한 캐싱 혜택 제공:
 * - 캐시 히트 시 즉시 반환 (네트워크 요청 없음)
 * - Request deduplication (동시 요청 시 하나만 실행)
 *
 * 캐싱 전략:
 * - staleTime: 1시간 (백엔드의 Cache-Control: max-age=3600와 정합)
 * - gcTime: 24시간 (메모리 효율성)
 * - retry: 5xx만 재시도 (4xx는 결정적 실패라 재시도 의미 없음)
 *
 * 에러 알럿은 `useWebsiteErrorAlert('preview')`에 위임 — preview/generate가
 * 동일한 AlertGroup 매핑·메시지·로그 payload를 공유한다.
 *
 * 에러/`isExtractable=false` 케이스는 throw하지 않고 `null`을 반환한다 —
 * caller는 반환값으로 success path를 분기하면 된다.
 *
 * @returns fetchPreview - Website URL로 메타데이터를 조회하는 함수
 *
 * @example
 * ```tsx
 * const { fetchPreview } = useWebsitePreview();
 * const data = await fetchPreview(url);
 * if (!data) return;
 * ```
 */
export const useWebsitePreview = () => {
  const queryClient = useQueryClient();
  const alert = useWebsiteErrorAlert('preview');
  const [isLoading, setIsLoading] = useState(false);

  const fetchPreview = useCallback(
    async (url: string): Promise<WebsitePreviewApiResponse | null> => {
      // 캐시 키는 정규화된 URL을 사용해 동일 URL의 대소문자/trailing slash
      // 변형이 별도 캐시 엔트리를 만들지 않도록 한다.
      const validation = validateWebsiteUrl(url);
      const cacheKey = validation.isValid ? validation.normalizedUrl : url;

      setIsLoading(true);
      try {
        let data: WebsitePreviewApiResponse;
        try {
          data = await queryClient.fetchQuery({
            queryKey: ['website', 'preview', cacheKey],
            queryFn: () => websiteApi.getPreview(url),
            staleTime: 1000 * 60 * 60,
            gcTime: 1000 * 60 * 60 * 24,
            retry: (failureCount, error) => {
              const status =
                error instanceof AxiosError
                  ? error.response?.status
                  : undefined;
              // 4xx는 결정적 실패 — 재시도하면 latency만 늘어난다.
              if (status !== undefined && status >= 400 && status < 500) {
                return false;
              }
              return failureCount < 2;
            },
          });
        } catch (error) {
          alert.fromApiError(error);
          return null;
        }

        // 200 + isExtractable=false는 backend가 partial OG 메타로 응답한 케이스
        // (CSR/SPA 등). 다른 에러와 동일하게 alertDialog로 사용자에게 알리고
        // null을 반환해 caller의 success path를 차단한다 — preview 카드/확정
        // 버튼이 노출되지 않는다.
        if (data.isExtractable === false) {
          alert.forUnusableContent();
          return null;
        }

        return data;
      } finally {
        setIsLoading(false);
      }
    },
    [queryClient, alert],
  );

  return { fetchPreview, isLoading };
};
